import { inject, Injectable } from '@angular/core';
import { EMPTY, Observable, throwError } from 'rxjs';
import { catchError, expand, map, reduce } from 'rxjs/operators';
import { ApiService } from 'src/app/core/api/api.service';
import { INSPECTORS_GRID_ENDPOINTS } from 'src/app/core/api/endpoints/inspectors-grid.endpoints';
import { RegisterAsInspector } from 'src/app/models/enums/register-as.enum';

@Injectable({
  providedIn: 'root',
})
export class InspectorGridService {
  private api = inject(ApiService);

  inspectorFilesList: any = [];
  inspectorFile = {};
  fileLabel = {
    membership_license_of_the_SCE_file: 'رخصة عضوية الهيئة السعودية للمهندسين',
    experience_certificate_file: 'شهادة خبرة',
    cv_file: 'ملف السيرة الذاتية',
    prefabricated_building_inspection_course_file:
      ' شهادة حضور دورة" فحص المباني الجاهزة"',
    building_quality_inspection_course_file:
      'شهادة حضور دورة" فحص جودة البناء"',
    career_insurance_file: 'صورة التأمين المهني الهندسي',
  };
  file = {};

  createOrderFilesList(inspector) {
    this.inspectorFilesList = [];
    Object.entries(inspector).forEach(([key, value]: any[]) => {
      if (
        key === 'membership_license_of_the_SCE_file' ||
        key === 'experience_certificate_file' ||
        key === 'cv_file' ||
        key === 'prefabricated_building_inspection_course_file' ||
        key === 'building_quality_inspection_course_file' ||
        key === 'career_insurance_file'
      ) {
        this.inspectorFile = {
          label: this.fileLabel[key],
          inspectorlabel: key,
          name: value,
          exist: value ? true : false,
          progress: false,
          progressVal: 0,
          error: false,
        };
        this.inspectorFilesList.push(this.inspectorFile);
      }
    });
    return this.inspectorFilesList;
  }

  getCareerPaths(): Observable<any> {
    return this.api.get(INSPECTORS_GRID_ENDPOINTS.V2.CAREER_PATHS)
  }

  getExpertFocusAreas(): Observable<any> {
    return this.api
      .get(INSPECTORS_GRID_ENDPOINTS.V1.EXPERT_FOCUS_AREAS)
      .pipe(
        map((data: any) => {
          return data;
        }),
        catchError(this.handleError),
      );
  }

  getExperinceYears(): Observable<any> {
    return this.api.get(INSPECTORS_GRID_ENDPOINTS.V1.YEARS_OF_EXPERIENCE).pipe(
      map((data: any) => {
        return data;
      }),
      catchError(this.handleError),
    );
  }

  uploadInspectorFiles(data, certificateID): Observable<any> {
    return this.api.patch(
      INSPECTORS_GRID_ENDPOINTS.V1.USER_CERTIFICATE_DETAIL(certificateID),
      data,
      {
        reportProgress: true,
        observe: 'response',
      },
    );
  }

  deleteUserCertificate(certificateID): Observable<any> {
    return this.api.delete(
      INSPECTORS_GRID_ENDPOINTS.V1.USER_CERTIFICATE_DETAIL(certificateID),
    );
  }

  getInspectorsByPage(query) {
    return this.api
      .get(INSPECTORS_GRID_ENDPOINTS.V1.INSPECTORS, { params: query })
      .pipe(
        map((data: any) => {
          return data;
        }),
        catchError(this.handleError),
      );
  }

  getAllInspectors(query) {
    return this.api
      .get(INSPECTORS_GRID_ENDPOINTS.V1.INSPECTORS, { params: query })
      .pipe(
        expand((response: any) => {
          if (!response['links'].next) return EMPTY;
          const nextParams = response['links'].next
            .split('?')[1]
            .split('&')
            .reduce((acc: Record<string, string>, pair: string) => {
              const [k, v] = pair.split('=');
              acc[k] = v;
              return acc;
            }, {});
          return this.api.get(INSPECTORS_GRID_ENDPOINTS.V1.INSPECTORS, {
            params: nextParams,
          });
        }),
        reduce((acc, current) => acc.concat(current['results']), []),
      );
  }

  getAccountInfo(): Observable<any> {
    return this.api.get(INSPECTORS_GRID_ENDPOINTS.V1.ACCOUNT_INFO).pipe(
      map((data: any) => {
        return data;
      }),
      catchError(this.handleError),
    );
  }

  getCertificates(): Observable<any> {
    return this.api.get(INSPECTORS_GRID_ENDPOINTS.V1.CERTIFICATES).pipe(
      map((data: any) => {
        return data;
      }),
      catchError(this.handleError),
    );
  }

  addCertificate(certificate): Observable<any> {
    return this.api.post(
      INSPECTORS_GRID_ENDPOINTS.V1.CERTIFICATES,
      certificate,
    );
  }

  deleteCertificate(certificateID): Observable<any> {
    return this.api.delete(
      INSPECTORS_GRID_ENDPOINTS.V1.CERTIFICATE_DETAIL(certificateID),
    );
  }

  updateCertificate(certificateID, data): Observable<any> {
    return this.api.put(
      INSPECTORS_GRID_ENDPOINTS.V1.CERTIFICATE_DETAIL_NO_SLASH(certificateID),
      data,
    );
  }

  updateInspectorsData(id, inspector): Observable<any> {
    return this.api
      .put(INSPECTORS_GRID_ENDPOINTS.V1.INSPECTOR_DETAIL(id), inspector)
      .pipe(
        map((data: any) => {
          return data;
        }),
      );
  }

  getRegistrationAs(): Observable<any> {
    return this.api.get(INSPECTORS_GRID_ENDPOINTS.V2.REGISTRATION_AS).pipe(
      map((data: any) => {
        return data;
      }),
      catchError(this.handleError),
    );
  }

  getInspectorsStats(params): Observable<any> {
    return this.api
      .get(INSPECTORS_GRID_ENDPOINTS.V1.INSPECTORS_STATS, { params: params })
      .pipe(
        map((data: any) => {
          return data;
        }),
        catchError(this.handleError),
      );
  }

  getFacilities(): Observable<any> {
    return this.api.get(INSPECTORS_GRID_ENDPOINTS.V1.FACILITIES);
  }

  isEditable(scope) {
    return scope.data.registration_in_ayen_as == RegisterAsInspector.facility
      ? true
      : false;
  }

  booleanCellStyle(params) {
    if (params.value) {
      return { backgroundColor: '#379B79' };
    }
    return { backgroundColor: '#D9727F' };
  }

  goOrders(params) {
    return `<a href="/pages/orders/orders-list?page=1&order_inspector=${params.data.full_name}"target="_blank">${params.value}</a>`;
  }

  handleError(error) {
    let errorMessage;
    if (error.error instanceof ErrorEvent) {
      errorMessage = `Error: ${error.error.message}`;
    } else {
      errorMessage = `Error Code: ${error.status}\nMessage: ${error.message}`;
    }
    return throwError(errorMessage);
  }

  exsepStatusProfile() {
    return this.api.get(INSPECTORS_GRID_ENDPOINTS.V1.EXSEP_PROFILE_STATUS);
  }

  getUserGroups(): Observable<any> {
    return this.api.get(INSPECTORS_GRID_ENDPOINTS.V1.FILTERED_GROUPS);
  }
}
